// src/features/streams/subaccounts/SubAccountStreamsPage.tsx

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Group, Loader, Stack, Switch, Text, TextInput, Title } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useNavigate } from '@tanstack/react-router';
import { IconAlertCircle, IconSearch } from '@tabler/icons-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { StreamsViewSwitch } from '../components/StreamsViewSwitch';
import { SubAccountStreamsGrid } from './SubAccountStreamsGrid';
import { SubAccountStreamUtils } from './SubAccountStreamUtils';
import {
  useSubAccountLiveStatus,
  useSubAccountStreams,
  useSubAccountStreamsAccess,
} from './useSubAccountStreams';
import type { IBackendLiveData } from '@/lib/types/Customer';
import type { SubAccountStreamRow, SubAccountStreamSortKey } from '@/lib/types/Stream';
import type { SortDirection } from '@/lib/types/common';

/**
 * Master-account monitoring view: every active stream across all sub-accounts
 * with polled live/recording status. Read-only by design — clicking a stream
 * switches into that sub-account and opens the stream detail page.
 */
export function SubAccountStreamsPage() {
  const { customerInfo, subcustomers, setSelectedCId, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const canView = useSubAccountStreamsAccess();

  const { streams, isLoading, isError, error } = useSubAccountStreams(canView);
  const { liveStatusMap } = useSubAccountLiveStatus(canView);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText] = useDebouncedValue(searchText, 150);
  const [liveOnly, setLiveOnly] = useState(false);
  const [sort, setSort] = useState<{ key: SubAccountStreamSortKey; direction: SortDirection }>({
    key: 'liveStatus',
    direction: 'desc',
  });

  // Opening a stream switches selectedCId to the sub-account, which flips
  // canView false while this page is still mounted — the ref keeps the guard
  // below from racing (and beating) the detail navigation.
  const openingStreamRef = useRef(false);

  // Guard: bounce non-eligible contexts (e.g. user switched to a sub-account)
  useEffect(() => {
    if (!authLoading && !canView && !openingStreamRef.current) {
      navigate({ to: '/streams' });
    }
  }, [authLoading, canView, navigate]);

  const backendMap = useMemo(() => {
    const map = new Map<number, IBackendLiveData>();
    for (const b of customerInfo?.liveBackends ?? []) {
      map.set(b.backendId, b);
    }
    return map;
  }, [customerInfo?.liveBackends]);

  const accountLabelMap = useMemo(
    () => SubAccountStreamUtils.buildAccountLabelMap(subcustomers),
    [subcustomers],
  );

  const masterCId = customerInfo?.customer.cfg.cId ?? null;

  const rows = useMemo(
    () =>
      SubAccountStreamUtils.buildRows(
        SubAccountStreamUtils.excludeMasterStreams(streams, masterCId),
        liveStatusMap,
        backendMap,
        accountLabelMap,
      ),
    [streams, masterCId, liveStatusMap, backendMap, accountLabelMap],
  );

  const summary = useMemo(() => SubAccountStreamUtils.summarize(rows), [rows]);

  const visibleRows = useMemo(() => {
    const filtered = SubAccountStreamUtils.filterRows(rows, {
      liveOnly,
      searchText: debouncedSearchText,
    });
    return SubAccountStreamUtils.sortRows(filtered, sort.key, sort.direction);
  }, [rows, liveOnly, debouncedSearchText, sort]);

  const handleSort = useCallback((key: SubAccountStreamSortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        // Live-first and newest-first read naturally descending
        : { key, direction: key === 'liveStatus' || key === 'published' || key === 'updated' ? 'desc' : 'asc' },
    );
  }, []);

  const handleOpenStream = useCallback(
    (row: SubAccountStreamRow) => {
      openingStreamRef.current = true;
      setSelectedCId(row.cId);
      navigate({ to: '/streams/$id', params: { id: row.displayId } });
    },
    [setSelectedCId, navigate],
  );

  if (isLoading || (authLoading && !canView)) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading sub-account streams...</Text>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error?.message ?? 'Failed to load sub-account streams'}
      </Alert>
    );
  }

  return (
    <Stack gap="md" h="100%" style={{ flex: 1, minHeight: 0 }}>
      <Group justify="space-between" align="center">
        <Title order={2}>Sub-Account Streams</Title>
        <StreamsViewSwitch active="subaccounts" />
      </Group>

      <Group justify="space-between" align="center" wrap="wrap">
        <Group gap="md">
          <TextInput
            placeholder="Search streams or accounts..."
            leftSection={<IconSearch size={16} />}
            value={searchText}
            onChange={(e) => setSearchText(e.currentTarget.value)}
            w={280}
          />
          <Switch
            label="Live only"
            checked={liveOnly}
            onChange={(e) => setLiveOnly(e.currentTarget.checked)}
          />
        </Group>
        <Text size="sm" c="dimmed">
          {summary.liveCount} live · {summary.recordingCount} recording · {summary.streamCount}{' '}
          {summary.streamCount === 1 ? 'stream' : 'streams'} across {summary.accountCount}{' '}
          {summary.accountCount === 1 ? 'sub-account' : 'sub-accounts'}
        </Text>
      </Group>

      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <SubAccountStreamsGrid
          rows={visibleRows}
          sortKey={sort.key}
          sortDirection={sort.direction}
          onSort={handleSort}
          onOpenStream={handleOpenStream}
          hasAnyStreams={rows.length > 0}
        />
      </div>
    </Stack>
  );
}
